import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Input,
  Button,
  Alert,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { downloadNotaDinas } from '../tenderOpeningApi';
import { exportHpsExcel } from './tenderOpeningExport';
import { formatDate, formatDateTime } from '../tenderOpeningFormatters';
import { HeaderTenderCard, CargoRequirementCard } from './TenderInfoCards';

// Tender Opening Detail > Sending Request HPS — layout mengikuti desain acuan:
//   1. Pilih Tujuan (per cargo/CDU) + matriks Crude × Quantity (checkbox) dengan
//      "Tambah Kolom" quantity baru. Matriks adalah lembar kerja untuk lampiran
//      Excel — TIDAK dipersist ke backend (kontrak belum punya field-nya);
//      isinya masuk ke Export To Excel di bawah.
//   2. Form "Nota Dinas Permohonan HPS Market & Freight Cost" — tetap memakai
//      endpoint kontrak 3/4 (Draft upsert + Send yang menggenerate
//      hpsMarketNumber/requestDate dan mengunci tab, 422 saat diulang).
//   3. Export To Excel — lampiran Nota Dinas (matriks + cargo requirement).
//   4. Informasi Header Tender + Cargo Requirement (read-only, echo Planning).

// "2026-07-19T17:00:00Z" -> "2026-07-20" for the <input type="date"> value.
// Wall-clock Jakarta, BUKAN toISOString(): server menyimpan tanggal dengan
// offset +07:00, jadi slice UTC akan mundur satu hari.
const toDateInputValue = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' });
};

// Gaya header navy (mengikuti tabel-tabel crude lain); per-<th> karena
// Bootstrap menimpa background thead lewat CSS variable.
const navyTh = {
  backgroundColor: '#1e3a56',
  color: '#ffffff',
  whiteSpace: 'nowrap',
};

const SendRequestHpsTab = ({
  record,
  detail,
  sent,
  editable,
  onSaveDraft,
  onSend,
  savingDraft,
  sending,
  // Freight Availability (pindahan dari tab Freight yang dihapus — desain
  // acuan menggabungkannya ke tab HPS "Market & Freight Cost"). Step checklist
  // BE-nya tetap terpisah: kontrak endpoint 8, generate nomor/tanggal.
  freightSent = false,
  freightEditable = false,
  onSendFreight,
  sendingFreight = false,
}) => {
  // `hps` is null until the BE generates it on Submit (contract: hpsRequest).
  const hps = detail?.hps || null;
  const freight = detail?.freight || null;
  const cargos = detail?.requirement?.cargos || [];
  const readOnly = !editable;

  // ---- Matriks Crude × Quantity (FE-only, bahan Export To Excel) -----------
  const [cargoId, setCargoId] = useState(null);
  const selectedCargo = cargos.find(c => c.id === cargoId) || cargos[0] || null;

  const [qtyCols, setQtyCols] = useState([]);
  const [checkedQty, setCheckedQty] = useState({});
  const [newQty, setNewQty] = useState('');

  // Reset kolom & centang saat tujuan (cargo) berganti: kolom = max capacity
  // unik milik crude cargo itu, default tercentang di kapasitasnya sendiri.
  useEffect(() => {
    if (!selectedCargo) {
      setQtyCols([]);
      setCheckedQty({});
      return;
    }
    const caps = [
      ...new Set(
        (selectedCargo.crudes || [])
          .map(cr => Number(cr.maxCapacity))
          .filter(n => Number.isFinite(n) && n > 0)
      ),
    ].sort((a, b) => a - b);
    setQtyCols(caps);
    const init = {};
    (selectedCargo.crudes || []).forEach(cr => {
      const cap = Number(cr.maxCapacity);
      if (Number.isFinite(cap) && cap > 0) init[`${cr.id}|${cap}`] = true;
    });
    setCheckedQty(init);
  }, [selectedCargo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleQty = (crudeId, qty) =>
    setCheckedQty(prev => ({
      ...prev,
      [`${crudeId}|${qty}`]: !prev[`${crudeId}|${qty}`],
    }));

  const handleAddQtyColumn = () => {
    const qty = Number(newQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity harus angka lebih dari 0.');
      return;
    }
    if (qtyCols.includes(qty)) {
      toast.info(`Kolom quantity ${qty} sudah ada.`);
      return;
    }
    setQtyCols(prev => [...prev, qty].sort((a, b) => a - b));
    setNewQty('');
  };

  // ---- Form Nota Dinas (kontrak 3/4) ----------------------------------------
  const [form, setForm] = useState({
    notaDinasTitle: hps?.notaDinasTitle || '',
    notaDinasNo: hps?.notaDinasNo || '',
    notaDinasDate: toDateInputValue(hps?.notaDinasDate),
    file: null,
  });

  // Re-sync the form with server truth whenever Detail reloads (after a
  // successful Save Draft / Send) — not on every unrelated re-render.
  useEffect(() => {
    setForm({
      notaDinasTitle: hps?.notaDinasTitle || '',
      notaDinasNo: hps?.notaDinasNo || '',
      notaDinasDate: toDateInputValue(hps?.notaDinasDate),
      file: null,
    });
  }, [hps?.notaDinasTitle, hps?.notaDinasNo, hps?.notaDinasDate]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const buildPayload = () => ({
    notaDinasTitle: form.notaDinasTitle || undefined,
    notaDinasNumber: form.notaDinasNo || undefined,
    notaDinasDate: form.notaDinasDate || undefined,
    file: form.file || undefined,
  });

  const handleSaveDraft = () => onSaveDraft(buildPayload());

  const handleSend = () => {
    if (!form.notaDinasTitle || !form.notaDinasNo || !form.notaDinasDate) {
      toast.error(
        'Judul, Nomor, dan Tanggal Nota Dinas wajib diisi untuk Kirim Permohonan HPS.'
      );
      return;
    }
    // Kontrak §4: file wajib kecuali Draft sebelumnya sudah mengunggah satu.
    if (!form.file && !hps?.notaDinasDocument) {
      toast.error('File Nota Dinas wajib diunggah.');
      return;
    }
    onSend(buildPayload());
  };

  // Contract §4a — one document per TenderOpening, no documentId needed.
  const handlePreviewNotaDinas = async () => {
    try {
      const blob = await downloadNotaDinas(record.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Preview unavailable: ${error}`);
    }
  };

  const handleDownloadNotaDinas = async () => {
    try {
      const blob = await downloadNotaDinas(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = hps?.notaDinasDocument || 'nota-dinas';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Download unavailable: ${error}`);
    }
  };

  const handleExport = () =>
    exportHpsExcel(
      { cargo: selectedCargo, qtyCols, checked: checkedQty },
      cargos,
      record?.procId
    );

  return (
    <div>
      {/* ---- 1. Pilih Tujuan + matriks Crude × Quantity ---- */}
      <Card className='border shadow-none mb-3'>
        <CardBody>
          <Row className='align-items-center g-2 mb-3'>
            <Col xs='auto'>
              <Label className='form-label mb-0'>Pilih Tujuan</Label>
            </Col>
            <Col lg={5} md={8}>
              <Input
                type='select'
                value={selectedCargo?.id || ''}
                onChange={e => setCargoId(e.target.value)}
              >
                {cargos.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.plant} {c.unit}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          <div className='table-responsive border rounded'>
            <Table className='table-sm align-middle mb-0'>
              <thead>
                <tr>
                  <th style={{ ...navyTh, width: 50 }}>No</th>
                  <th style={navyTh}>Crude</th>
                  {qtyCols.map(q => (
                    <th key={q} style={navyTh} className='text-center'>
                      {q}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!selectedCargo || !(selectedCargo.crudes || []).length ? (
                  <tr>
                    <td
                      colSpan={2 + qtyCols.length}
                      className='text-center text-muted py-3'
                    >
                      No crude data
                    </td>
                  </tr>
                ) : (
                  selectedCargo.crudes.map((cr, idx) => (
                    <tr key={cr.id}>
                      <td className='text-muted'>{idx + 1}</td>
                      <td className='fw-semibold'>{cr.crudeName}</td>
                      {qtyCols.map(q => (
                        <td key={q} className='text-center'>
                          <Input
                            type='checkbox'
                            className='form-check-input'
                            checked={!!checkedQty[`${cr.id}|${q}`]}
                            disabled={readOnly}
                            onChange={() => toggleQty(cr.id, q)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {!readOnly && (
            <div className='d-flex align-items-center gap-2 mt-3'>
              <Label className='form-label mb-0 fw-semibold fs-13'>
                Tambah Quantity Baru:
              </Label>
              <Input
                type='number'
                min={1}
                style={{ maxWidth: 130 }}
                bsSize='sm'
                placeholder='e.g. 800'
                value={newQty}
                onChange={e => setNewQty(e.target.value)}
              />
              <Button color='primary' size='sm' onClick={handleAddQtyColumn}>
                <i className='ri-add-line align-bottom me-1'></i>
                Tambah Kolom
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- 2. Form Nota Dinas ---- */}
      {!sent && (
        <Alert color='warning' className='d-flex align-items-center'>
          <i className='ri-alert-line fs-5 me-2'></i>
          Silakan lengkapi formulir permohonan HPS Market &amp; Freight Cost
          sebelum mengirim.
        </Alert>
      )}

      {/* HPS Information — hidden until the BE generates it on Send. */}
      {hps && (hps.hpsNumber || hps.hpsRequestDate) && (
        <Card className='border shadow-none mb-3'>
          <CardBody>
            <p className='form-label mb-2 fs-13 text-uppercase fw-semibold'>
              HPS Information
            </p>
            <Row className='g-3'>
              <Col lg={6} sm={6}>
                <Label className='form-label'>HPS Market Number</Label>
                <Input value={hps.hpsNumber || '-'} readOnly disabled />
              </Col>
              <Col lg={6} sm={6}>
                <Label className='form-label'>Request Date</Label>
                <Input
                  value={
                    hps.hpsRequestDate
                      ? formatDateTime(hps.hpsRequestDate)
                      : '-'
                  }
                  readOnly
                  disabled
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      <Card className='border shadow-none mb-3'>
        <CardBody>
          <div className='d-flex align-items-start flex-wrap gap-2 mb-3'>
            <div className='flex-grow-1'>
              <h6 className='fw-semibold mb-1'>
                <i className='ri-file-text-line align-bottom me-2'></i>
                Nota Dinas Permohonan HPS Market &amp; Freight Cost
              </h6>
              <p className='text-muted fs-12 mb-0'>
                Lengkapi informasi nota dinas berikut sebagai dasar permohonan
                penetapan harga perkiraan sendiri ke tim Pricing Analyst.
              </p>
            </div>
            <small className='text-muted'>
              Tanda (<span className='text-danger'>*</span>) Wajib diisi
            </small>
          </div>

          <Row className='g-3 align-items-center'>
            <Col md={3}>
              <Label className='form-label mb-0'>
                Judul Nota Dinas <span className='text-danger'>*</span> :
              </Label>
            </Col>
            <Col md={9}>
              <Input
                value={form.notaDinasTitle}
                onChange={e => setField('notaDinasTitle', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={
                  readOnly ? '-' : 'Masukkan judul nota dinas permohonan HPS...'
                }
              />
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>
                Nomor Nota Dinas <span className='text-danger'>*</span> :
              </Label>
            </Col>
            <Col md={9}>
              <Input
                value={form.notaDinasNo}
                onChange={e => setField('notaDinasNo', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={readOnly ? '-' : 'e.g. ND-019/KPI/PROC/2026'}
              />
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>
                Tanggal Nota Dinas <span className='text-danger'>*</span> :
              </Label>
            </Col>
            <Col md={9}>
              {readOnly ? (
                <Input
                  value={
                    form.notaDinasDate ? formatDate(form.notaDinasDate) : '-'
                  }
                  readOnly
                  disabled
                />
              ) : (
                <Input
                  type='date'
                  value={form.notaDinasDate}
                  onChange={e => setField('notaDinasDate', e.target.value)}
                />
              )}
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>
                File Nota Dinas Permintaan HPS Alpha &amp; Freight Cost{' '}
                <span className='text-danger'>*</span> :
              </Label>
            </Col>
            <Col md={9}>
              <div className='d-flex align-items-center gap-2 border rounded px-3 py-2 bg-light'>
                <i className='ri-attachment-2 text-muted'></i>
                <span className='flex-grow-1 text-muted fst-italic fs-13 text-truncate'>
                  {form.file
                    ? form.file.name
                    : hps?.notaDinasDocument
                      ? hps.notaDinasDocument
                      : 'Belum ada file nota dinas yang diunggah'}
                </span>
                {hps?.notaDinasDocument && !form.file && (
                  <>
                    <button
                      type='button'
                      className='btn btn-soft-secondary btn-sm btn-icon'
                      title='Preview'
                      onClick={handlePreviewNotaDinas}
                    >
                      <i className='ri-eye-line align-bottom'></i>
                    </button>
                    <button
                      type='button'
                      className='btn btn-soft-primary btn-sm btn-icon'
                      title='Download'
                      onClick={handleDownloadNotaDinas}
                    >
                      <i className='ri-download-2-line align-bottom'></i>
                    </button>
                  </>
                )}
                {!readOnly && (
                  <label className='btn btn-light border btn-sm mb-0'>
                    <i className='ri-upload-2-line align-bottom me-1'></i>
                    Browse File...
                    <input
                      type='file'
                      hidden
                      onChange={e =>
                        setField('file', e.target.files?.[0] || null)
                      }
                    />
                  </label>
                )}
              </div>
            </Col>
          </Row>

          <div className='d-flex gap-2 justify-content-end mt-3'>
            <Button
              color='secondary'
              outline
              disabled={readOnly || sent || savingDraft || sending}
              title={readOnly ? 'View only' : undefined}
              onClick={handleSaveDraft}
            >
              <i className='ri-draft-line align-bottom me-1'></i>
              {savingDraft ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button
              color='success'
              disabled={readOnly || sent || sending || savingDraft}
              title={readOnly ? 'View only' : undefined}
              onClick={handleSend}
            >
              <i className='ri-send-plane-line align-bottom me-1'></i>
              {sent
                ? 'Permohonan HPS Terkirim'
                : sending
                  ? 'Mengirim...'
                  : 'Kirim Permohonan HPS'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ---- 3. Freight Availability (pindahan tab Freight) ---- */}
      <Card className='border shadow-none mb-3'>
        <CardBody>
          <div className='d-flex align-items-start flex-wrap gap-2'>
            <div className='flex-grow-1'>
              <h6 className='fw-semibold mb-1'>
                <i className='ri-ship-2-line align-bottom me-2'></i>
                Request Freight Availability
              </h6>
              <p className='text-muted fs-12 mb-0'>
                Kirim permintaan Freightcost Availability — nomor dan tanggal
                request digenerate otomatis oleh sistem saat dikirim.
              </p>
            </div>
            <Button
              color='success'
              outline
              disabled={!freightEditable || freightSent || sendingFreight}
              title={freightEditable ? undefined : 'View only'}
              onClick={onSendFreight}
            >
              <i className='ri-send-plane-line align-bottom me-1'></i>
              {freightSent
                ? 'Request Freightcost Terkirim'
                : sendingFreight
                  ? 'Mengirim...'
                  : 'Send Request Freightcost'}
            </Button>
          </div>
          {freight && (freight.freightNumber || freight.freightRequestDate) && (
            <Row className='g-3 mt-1'>
              <Col lg={6} sm={6}>
                <Label className='form-label'>Freightcost Request Number</Label>
                <Input value={freight.freightNumber || '-'} readOnly disabled />
              </Col>
              <Col lg={6} sm={6}>
                <Label className='form-label'>Freightcost Request Date</Label>
                <Input
                  value={
                    freight.freightRequestDate
                      ? formatDateTime(freight.freightRequestDate)
                      : '-'
                  }
                  readOnly
                  disabled
                />
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>

      {/* ---- 4. Export To Excel ---- */}
      <Alert color='info' className='d-flex align-items-center'>
        <i className='ri-information-line fs-5 me-2'></i>
        Informasi detail rencana Pengadaan Import Minyak Mentah/Kondensat
        dibawah ini dapat diunduh dalam format excel untuk dapat dijadikan
        lampiran dalam pengajuan Nota Dinas Permintaan HPS Alpha dan Freight
        Cost
      </Alert>
      <div className='text-end mb-3'>
        <Button color='success' onClick={handleExport}>
          <i className='ri-download-2-line align-bottom me-1'></i>
          Export To Excel
        </Button>
      </div>

      {/* ---- 4. Informasi Header Tender ---- */}
      <HeaderTenderCard record={record} detail={detail} />

      {/* ---- 5. Cargo Requirement (read-only echo Planning) ---- */}
      <CargoRequirementCard cargos={cargos} />
    </div>
  );
};

export default SendRequestHpsTab;
